import type React from 'react';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { Sphere } from 'types/global';
import type { Schema, SchemaProperty, SchemaSection } from 'types/schema';
import { membersSectionSlot } from '../SphereMembers/membersSectionSlot';
import { identitySectionSlot } from './identity/SphereIdentitySection';
import { metricsSectionSlot } from './slots/metrics/metricsSectionSlot';

/**
 * Slot contract for schema sections (specs/schema-sections.md § Layout contract).
 *
 * A slot lets a later feature render its own content at the top of a section. Slots render only
 * `ctx.properties` (and `ctx.schemaType.properties`, which is the same viewer-visible set), never
 * the full schema, so a slot cannot show a field the viewer is not allowed to read.
 *
 * `mode`/`editable` are the section-level answer: `view` when the sphere is in view mode or the
 * viewer can write none of the section's properties. A section with no properties keeps the
 * sphere-level answer. `editable` covers property writes only; a slot that writes anything else
 * (membership, relationships) makes its own permission check.
 */
export interface SphereSectionSlotContext {
  sphere: Sphere;
  /** Schema whose `properties` are the viewer-visible set. */
  schemaType: Schema;
  /**
   * `label` is en-US source text for every section, the implicit Details group included; a slot
   * shows `getTranslation(i18n, section.label_key, section.label)`, as the section heading does.
   */
  section: SchemaSection;
  /** Visible properties resolved to this section. */
  properties: SchemaProperty[];
  mode: 'edit' | 'view';
  editable: boolean;
  /**
   * The write-blocked names among `properties`, never wider. A slot gates each field it owns
   * against this set (disabled with a lock), as `WholeAspectRenderer` does, even when `editable`.
   */
  writeBlockedProps: ReadonlySet<string>;
}

/**
 * A slot whose content is fetched supplies its own external store so `appliesTo` and
 * `ownsProperty` can stay synchronous (they `peek` the store). Additive: slots without async
 * data omit it.
 */
export interface SphereSectionSlotDataSource {
  /** Start or reuse this slot's read for the sphere. Idempotent; never writes. */
  load(args: { sphereId: string; graphClient: ApiClient; force?: boolean }): void;
  /** External-store subscription; fires when this slot's data for any sphere changes. */
  subscribe(listener: () => void): () => void;
  /** Drop cached data for a sphere (called after a successful editor save). */
  invalidate?(sphereId: string): void;
}

export interface SphereSectionSlot {
  /** When true the section renders even with zero properties. */
  appliesTo(ctx: SphereSectionSlotContext): boolean;
  /** Properties the slot renders itself; the property grid omits them. */
  ownsProperty?(prop: SchemaProperty, ctx: SphereSectionSlotContext): boolean;
  Component: React.ComponentType<SphereSectionSlotContext>;
  dataSource?: SphereSectionSlotDataSource;
}

export const SPHERE_SECTION_SLOTS: Readonly<Record<string, SphereSectionSlot>> = Object.freeze({
  identity: identitySectionSlot,
  members: membersSectionSlot,
  metrics: metricsSectionSlot,
});
